/**
 * Hook for fetching and managing AWS cost data
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import type { CostsResponse, TimeRange } from '../types/dashboard';
import { fetchDashboardCosts } from '../lib/dashboard-api';
import { useAuth } from '../components/Auth';

// Auto-refresh interval for costs (15 minutes - costs don't change frequently)
const REFRESH_INTERVAL = 15 * 60 * 1000;

interface UseDashboardCostsOptions {
  autoRefresh?: boolean;
}

interface UseDashboardCostsResult {
  data: CostsResponse | null;
  isLoading: boolean;
  error: string | null;
  refetch: () => void;
  lastUpdated: Date | null;
}

/**
 * Hook to fetch AWS cost data for a given time range
 *
 * @param timeRange - The time range to fetch costs for (1d, 3d, 7d, 30d)
 * @param options - Optional configuration (autoRefresh: boolean)
 * @returns Object with cost data, loading state, error, refetch function, and lastUpdated
 */
export function useDashboardCosts(
  timeRange: TimeRange,
  options: UseDashboardCostsOptions = {},
): UseDashboardCostsResult {
  const { autoRefresh = true } = options;
  const { getAuthHeader, isAuthenticated } = useAuth();
  const [data, setData] = useState<CostsResponse | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [fetchTrigger, setFetchTrigger] = useState(0);
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);

  const refetch = useCallback(() => {
    setFetchTrigger((prev) => prev + 1);
  }, []);

  useEffect(() => {
    if (!isAuthenticated) {
      setIsLoading(false);
      setError('Authentication required');
      return;
    }

    const abortController = new AbortController();

    async function loadCosts() {
      setIsLoading(true);
      setError(null);

      try {
        const authHeader = getAuthHeader();
        if (!authHeader) {
          throw new Error('No authentication token available');
        }

        const costs = await fetchDashboardCosts(
          timeRange,
          { Authorization: authHeader },
          abortController.signal,
        );
        setData(costs);
        setLastUpdated(new Date());
      } catch (err) {
        if (err instanceof Error && err.name === 'AbortError') {
          return; // Ignore abort errors
        }
        setError(
          err instanceof Error ? err.message : 'Failed to fetch cost data',
        );
      } finally {
        setIsLoading(false);
      }
    }

    loadCosts();

    return () => {
      abortController.abort();
    };
  }, [timeRange, isAuthenticated, getAuthHeader, fetchTrigger]);

  // Auto-refresh effect
  useEffect(() => {
    if (!autoRefresh || !isAuthenticated) {
      return;
    }

    intervalRef.current = setInterval(() => {
      refetch();
    }, REFRESH_INTERVAL);

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [autoRefresh, isAuthenticated, refetch]);

  return { data, isLoading, error, refetch, lastUpdated };
}
